/**
 * Parcel size presets, as the seller's form needs them (P25028-132).
 *
 * The seller picks one of three named boxes and types a real weight, instead of
 * naming a postage price. The price then comes from the carrier at checkout, for
 * the parcel the seller actually described.
 *
 * ================ The server holds the same numbers, and it decides ================
 *
 * `server/api-util/parcelSize.js` is the copy that matters: a listing can also be
 * created by bulk upload, which never loads this form, and the API can be reached
 * without a browser at all. This file exists so the seller gets an answer while
 * they type rather than after they press save. Keep the two in step — the numbers
 * are pinned by a test on each side, so a change to one alone fails a test.
 *
 * The weight caps are the client's numbers, narrowed by what the carriers on the
 * Shippo account will lift, and by what a drop-off shop will take (P25028-271).
 * A DPD Pickup shop takes parcels under 20 kg, so Medium's cap is 19.9. See
 * `CARRIER_LIMITS` and `SHOP_LIMITS` in `server/api-util/shippo.js`.
 */

/**
 * The three segments. `maxWeightKg: null` means no cap at all, which only the
 * last one has.
 *
 * `offPlatform` is the important flag: that one gets no carrier price, no
 * shipping line on the bill, and a plain notice telling the buyer and the seller
 * to arrange postage between themselves.
 *
 * The client named four, with a "Large" between Medium and this. It was built and
 * then merged away, because **no carrier on the account quotes a parcel that
 * size** — DPD refuses 88x50x50 cm, Evri refuses past about 120 cm — so Large
 * behaved identically to `tooBigHeavy` in every respect. Two names for one
 * behaviour is a choice the seller cannot get right.
 */
export const PARCEL_SIZES = {
  small: {
    key: 'small',
    maxWeightKg: 2,
    dimensionsCm: { length: 45, width: 35, height: 16 },
    offPlatform: false,
  },
  // P25028-271: just inside a DPD Pickup shop's limits, because Causee postage is
  // drop-off only. See `PARCEL_SIZES` in server/api-util/parcelSize.js.
  medium: {
    key: 'medium',
    maxWeightKg: 19.9,
    dimensionsCm: { length: 60, width: 46, height: 46 },
    offPlatform: false,
  },
  tooBigHeavy: {
    key: 'tooBigHeavy',
    maxWeightKg: null,
    dimensionsCm: null,
    offPlatform: true,
  },
};

/**
 * The box a segment stands for, in centimetres, or null.
 *
 * These are the same numbers the server quotes with, so a seller printing a label
 * sees the parcel the buyer was actually charged for. Keep them in step with
 * `PARCEL_SIZES` in `server/api-util/parcelSize.js` — a test on each side pins
 * them.
 */
export const dimensionsCmFor = size => PARCEL_SIZES[size]?.dimensionsCm || null;

/** The segments in the order the seller is offered them. */
export const PARCEL_SIZE_KEYS = ['small', 'medium', 'tooBigHeavy'];

/** A weight small enough to be a slip of the finger, and one nobody would post. */
export const MIN_PARCEL_WEIGHT_KG = 0.01;
export const MAX_PARCEL_WEIGHT_KG = 1000;

/** Is this one of the three segments? */
export const isParcelSize = size => PARCEL_SIZE_KEYS.includes(size);

/**
 * Is postage for this segment arranged directly between buyer and seller?
 *
 * An unknown or missing segment counts as off-platform, the same way round as on
 * the server: the worst case is a buyer asked to arrange postage, rather than
 * being charged for a parcel no carrier agreed to carry.
 */
export const isOffPlatformSize = size => !isParcelSize(size) || !!PARCEL_SIZES[size].offPlatform;

/**
 * Does this listing actually carry an off-platform parcel?
 *
 * The difference from `isOffPlatformSize` matters, and getting it wrong is
 * visible to buyers. That one answers "is this named segment off-platform", and
 * treats a name it does not know as off-platform on purpose — the safe answer
 * when something unexpected arrives.
 *
 * **A listing with no segment at all is a different thing.** Sixty-seven listings
 * were created before sellers were asked, and they are ordinary postable items.
 * Running them through `isOffPlatformSize` would tell every buyer to arrange
 * their own courier for a parcel a carrier would happily have taken.
 *
 * So: no segment means not off-platform. Use this wherever a stored listing is
 * being read, and the other one only where a segment has just been chosen.
 */
export const hasOffPlatformParcel = parcelSize => !!parcelSize && isOffPlatformSize(parcelSize);

/**
 * Does this listing offer postage without saying how big the parcel is?
 * (P25028-190)
 *
 * Listings made before sellers were asked for a size. They still sell, priced for
 * the standard parcel, so the seller is reminded to fix them rather than made to.
 * Kept in step with the same helper in `server/api-util/parcelSize.js`.
 */
export const needsParcelSize = listing => {
  const publicData = listing?.attributes?.publicData;
  return !!publicData?.shippingEnabled && !publicData?.parcelSize;
};

/** The heaviest weight this segment accepts, or null when there is no cap. */
export const maxWeightKgFor = size => PARCEL_SIZES[size]?.maxWeightKg ?? null;

/**
 * The lightest segment that could hold this weight.
 *
 * Only ever used to tell the seller which box to move to. Weight is all there is
 * to go on, so it is a suggestion they confirm, never a change made for them.
 */
export const suggestedSizeForWeight = weightKg => {
  const found = PARCEL_SIZE_KEYS.find(key => {
    const max = maxWeightKgFor(key);
    return max == null || weightKg <= max;
  });
  return found || 'tooBigHeavy';
};

/**
 * The parcel size a seller typed in a bulk upload spreadsheet, as a key
 * (P25028-191).
 *
 * Case, spaces and punctuation are ignored, and the start of the text is enough,
 * so "Small", "S", "small parcel" and the full line from the page's instructions
 * all mean Small.
 *
 * Returns a segment key, 'large' for a size we do not offer (so the seller can be
 * told why, rather than just "not recognised"), null for an empty cell, and
 * undefined when nothing matches.
 */
const PARCEL_SIZE_TYPED = [
  { key: 'tooBigHeavy', starts: ['toobig', 'toolarge', 'tooheavy', 'offplatform'] },
  { key: 'small', starts: ['small'], exact: ['s'] },
  { key: 'medium', starts: ['medium'], exact: ['m'] },
  // Not a size we offer. A seller who types it may think a courier will carry it.
  { key: 'large', starts: ['large'], exact: ['l'] },
];

export const toParcelSizeKey = value => {
  const typed = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!typed) return null;
  const hit = PARCEL_SIZE_TYPED.find(
    ({ starts = [], exact = [] }) =>
      exact.includes(typed) || starts.some(start => typed.startsWith(start))
  );
  return hit ? hit.key : undefined;
};
